import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { format, subMinutes, fromUnixTime } from 'date-fns';
import { task } from 'ember-concurrency';
import type FeatureConfigService from 'garaje/services/feature-config';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type StateService from 'garaje/services/state';

interface UnavailableResourcePopupArgs {
  selectedDate: Date;
  selectedTime?: number;
  selectedEndTime?: number;
  featureName: string;
  mapFloorName: string;
  resourceType: string;
  isReservedByCurrentUser: boolean;
  isAssignedToCurrentUser: boolean;
  hasAssignment: boolean;
  onReleaseResourceTask: () => Promise<void>;
  onBookResource: () => Promise<void>;
  onCancel: () => void;
  reservationStartTime?: string;
  reservationEndTime?: string;
}

export default class UnavailableResourcePopup extends Component<UnavailableResourcePopupArgs> {
  @service declare state: StateService;
  @service declare store: StoreService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare featureConfig: FeatureConfigService;
  get formattedDate(): string {
    const displayDate = subMinutes(this.args.selectedDate, this.state.minutesBetweenTimezones(this.args.selectedDate));
    return format(displayDate, 'MMMM dd, yyyy');
  }

  get isGRPartialDayEnabled(): boolean {
    return this.featureConfig.isEnabled('partialDayParking');
  }

  get isPartialDay(): boolean {
    return this.isGRPartialDayEnabled && this.args.selectedTime != null && this.args.selectedEndTime != null;
  }

  get formattedStartTime(): string {
    if (!this.isPartialDay) return '';

    // Explicitly check which value to use
    if (this.args.reservationStartTime) {
      // We have reservation time from API - it's an ISO string
      const start = new Date(this.args.reservationStartTime);
      const displayStart = subMinutes(start, this.state.minutesBetweenTimezones(start));
      return format(displayStart, 'h:mm a');
    } else {
      const start = fromUnixTime(this.args.selectedTime as number);
      const displayStart = subMinutes(start, this.state.minutesBetweenTimezones(start));
      return format(displayStart, 'h:mm a');
    }
  }

  get formattedEndTime(): string {
    if (!this.isPartialDay) return '';

    // Explicitly check which value to use
    if (this.args.reservationEndTime) {
      // We have reservation time from API - it's an ISO string
      const end = new Date(this.args.reservationEndTime);
      const displayEnd = subMinutes(end, this.state.minutesBetweenTimezones(end));
      return format(displayEnd, 'h:mm a');
    } else {
      // Fall back to selected time from date picker - it's a unix timestamp

      const end = fromUnixTime(this.args.selectedEndTime as number);
      const displayEnd = subMinutes(end, this.state.minutesBetweenTimezones(end));
      return format(displayEnd, 'h:mm a');
    }
  }

  get unavailabilityReason(): string {
    // A resource popup is unavailable if
    // 1. it is assigned to the current user
    //    - the current user can then reserve the resource
    // 2. it is reserved for the current user
    // 3. it is assigned to someone else
    // 4. it is reserved for someone else
    // 5. if it is assigned and reserved for someone else, then just show the assignment state
    if (this.args.isReservedByCurrentUser) {
      return 'Reserved for you';
    }

    if (this.args.isAssignedToCurrentUser) {
      return 'Assigned to you';
    }

    if (this.args.hasAssignment) {
      return 'Assigned to someone else';
    }

    return 'Reserved for someone else';
  }

  onBookResourceTask = task({ drop: true }, async () => {
    await this.args.onBookResource();
  });
}
